import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Box,
  Typography, Switch, Skeleton
} from '@mui/material';
import apiClient from '../../services/apiClient';

// 999.18483 — PUT /api/admin/users/:id/applications/:appId requires step-up (403 STEP_UP_REQUIRED).
function accessRefusalMessage(err) {
  if (err.data && err.data.error === 'STEP_UP_REQUIRED') {
    return 'Changing application access needs a recent two-factor check. Sign out and back in with your authenticator code, then try again.';
  }
  return err.message;
}

/**
 * 999.17683 — per-user application access for admins.
 *
 * Lists every ACTIVE application (GET /api/applications) merged with the
 * user's grants (GET /api/admin/users/:id/applications). The merge is
 * required: the user-applications query inner-joins user_applications
 * (KnexApplicationRepository.findUserApplications), so an application the
 * user never had a row for is simply ABSENT from that response and could
 * never be added from a list built out of it alone.
 *
 * A toggle calls the existing PUT /api/admin/users/:id/applications/:appId
 * with a strict boolean, then reloads both lists.
 */

// MySQL returns tinyint(1) for these columns, so knex hands back 1/0 rather
// than true/false. Read both spellings explicitly — never a truthiness test,
// so that a missing/unknown value reads as NOT granted instead of "maybe".
function isTrue(value) {
  return value === true || value === 1;
}

export default function ManageAccessDialog({ open, user, onClose }) {
  const [apps, setApps] = useState([]);
  // The user `apps`/`error` were actually loaded for. UserManagement mounts this
  // dialog permanently, so both survive a Close and a re-open for a DIFFERENT
  // admin target; without this stamp the previous user's switch rows paint into
  // the new user's dialog and a click writes a grant to the wrong user.
  const [loadedUserId, setLoadedUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const userId = user ? user.id : null;
  const showsCurrentUser = userId !== null && loadedUserId === userId;

  // The user the dialog is showing RIGHT NOW. `userId` captured inside an async
  // closure is the value of the render that created the closure, so a write that
  // settles after a Close/re-open cannot learn from it that the admin moved on;
  // a ref mutated on every user change can be read AFTER the await. Declared
  // before the load effect so it is up to date before any load for the new user
  // starts (effects run in declaration order).
  const currentUserIdRef = useRef(null);
  useEffect(() => {
    currentUserIdRef.current = open ? userId : null;
    // `savingId` disables EVERY switch. It is owned by the user it was set for,
    // and the write that set it may still be hanging (apiClient has no timeout),
    // so the new user's dialog would otherwise load with all switches greyed out
    // and no message. Deps are [open, userId] ONLY: a reloadKey refresh must not
    // reset a savingId that toggle's own finally still owns.
    setSavingId(null);
  }, [open, userId]);

  // Defense in depth against a superseded response. What prevents a WRONG-USER
  // write is the `loadedUserId` stamp + the render gate below + toggle's refusal:
  // without this guard a late response could only stamp its OWN user id, which
  // drops the dialog back to the skeleton (fail-closed), never paints or writes
  // another user's rows. `isCancelled` is what keeps that superseded response
  // from clobbering the state the current user's own load already put on screen.
  // Required (no default) so a caller cannot silently opt out of it.
  const load = useCallback(async (targetUserId, isCancelled) => {
    setLoading(true);
    setError('');
    try {
      const [allApps, userApps] = await Promise.all([
        apiClient.get('/api/applications'),
        apiClient.get(`/api/admin/users/${targetUserId}/applications`),
      ]);
      if (isCancelled()) return;

      // Fail loud rather than render "no access" from a malformed response —
      // an empty switch list is indistinguishable from a real revocation.
      if (!Array.isArray(allApps?.applications) || !Array.isArray(userApps?.applications)) {
        throw new Error('Unexpected response from the application access API');
      }

      const grantedById = new Map(
        userApps.applications.map((row) => [row.id, isTrue(row.is_authorized)])
      );

      setApps(
        allApps.applications
          .filter((app) => isTrue(app.is_active))
          .map((app) => ({
            id: app.id,
            appId: app.appId,
            name: app.name,
            granted: grantedById.get(app.id) === true,
          }))
      );
      setLoadedUserId(targetUserId);
    } catch (err) {
      if (isCancelled()) return;
      setError(err.message);
      setApps([]);
      setLoadedUserId(targetUserId);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !userId) return undefined;
    let cancelled = false;
    load(userId, () => cancelled);
    // Close, or a switch to another user, supersedes the in-flight load.
    return () => { cancelled = true; };
  }, [open, userId, reloadKey, load]);

  async function toggle(app) {
    // The rows on screen were loaded for `loadedUserId`; refuse to write one of
    // them against anybody else.
    if (!showsCurrentUser) return;
    const writeUserId = userId;
    const nextValue = !app.granted;
    setSavingId(app.id);
    setError('');
    try {
      await apiClient.put(`/api/admin/users/${writeUserId}/applications/${app.id}`, {
        is_authorized: nextValue,
      });
      // A write the admin has already navigated away from owns none of this
      // state any more: its success must not refresh, its failure must not paint,
      // and its finally must not re-enable switches while the user now on screen
      // may have a write of their own in flight. Compare against the ref, not the
      // closure's `userId` — that one is frozen at the render this call came from.
      if (currentUserIdRef.current !== writeUserId) return;
      // Reload through the effect so the refresh is cancellable like any other.
      setReloadKey((key) => key + 1);
    } catch (err) {
      if (currentUserIdRef.current !== writeUserId) return;
      setError(accessRefusalMessage(err));
    } finally {
      if (currentUserIdRef.current === writeUserId) setSavingId(null);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Manage Access
        {user && (
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && showsCurrentUser && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Wording pinned to the code: jwt-auth.js getUserApps() runs only in
            generateAccessToken/generateImpersonationToken, so `apps` is stamped
            into the access token at issue and consumers read the claim
            (e.g. juggler jwt-auth.js checks req.auth.apps). Not per-request. */}
        <Alert severity="info" sx={{ mb: 2 }}>
          Access is stamped into the user&apos;s access token when it is issued, so a
          change here takes effect the next time their token is refreshed (access
          tokens last 15 minutes by default) — not immediately.
        </Alert>

        {/* Never paint rows that belong to another user: until THIS user's load
            has landed there is nothing truthful to show but the skeleton. */}
        {loading || !showsCurrentUser ? (
          <Skeleton variant="rectangular" height={120} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {apps.length === 0 && !error && (
              <Typography variant="body2" color="text.secondary">
                No active applications.
              </Typography>
            )}
            {apps.map((app) => (
              <Box
                key={app.id}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  py: 1, borderBottom: '1px solid', borderColor: 'divider',
                }}
              >
                <Box>
                  <Typography variant="body2">{app.name}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                    {app.appId}
                  </Typography>
                </Box>
                {/* MUI v7 (7.3.9): Switch builds the SwitchBase input slot as
                    `input: { role: 'switch' }, ...(slotProps.input && { input: … })`
                    (@mui/material/Switch/Switch.js:283-289) and SwitchBase then does
                    `{ input: inputProps, ...slotProps }` (internal/SwitchBase.js:172-174).
                    So the deprecated `inputProps` aria-label is DROPPED — the switch has no
                    accessible name at all — and a `slotProps.input` that omits `role`
                    REPLACES the default role. Both keys must be passed together. */}
                <Switch
                  checked={app.granted}
                  disabled={savingId !== null}
                  onChange={() => toggle(app)}
                  slotProps={{ input: { role: 'switch', 'aria-label': `Access to ${app.name}` } }}
                />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
